import * as angular from "angular"
import { HoldingEvent } from "../api/types"
import { OmniaConfig } from "../config"
import { OmniaObjectType } from "../generated/enums"
import { I18nService } from "../i18n/types"
import { ReadWritePermissions, UserPermissions } from "../permissions/types"
import { AngularReactDialog, showAngularReactDialog } from "../react/reactAngularDialog"
import { ClientActivity, IOmniaShell, IOmniaShellDialogOptions } from "../shell"
import { IOmniaSession } from "../types"
import { isArrayEmpty } from "../utils/array"
import { loadScript, loadStylesheet } from "../utils/loader"
import { isObjectEmpty } from "../utils/objects"
import { isString, isUndefinedOrNull } from "../utils/utils"
import { UpdatableComponent } from "./component"
import { IDialogScope, IMessageBox, IModule } from "./types"

import ngRoute = angular.route


export class ShellService implements IOmniaShell {

    private $route: ngRoute.IRouteService

    private _currentRoute: string
    private _currentRouteParams: { [key: string]: any }

    disableMenu = false
    showIds = false

    public readonly mobile = isMobileOperatingSystem()

    private _currentModule: IModule

    private _dialogs = [] as { scope: IDialogScope }[]

    private _enableAutoRefresh = true
    private _refreshing: ng.IPromise<any>
    private _refreshPending: "auto" | "user" = undefined
    private _deferredAutoRefresh: {
        newEvents: HoldingEvent[]
    }


    static $inject = [
        "$timeout",
        "$q",
        "$rootScope",
        "$location",
        "$injector",
        "$uibModal",
        "blockUI",
        "OmniaSession",
        "OmniaI18n",
        "omniaConfig",
    ]

    constructor(
        private readonly $timeout: ng.ITimeoutService,
        private readonly $q: ng.IQService,
        private readonly $rootScope: ng.IRootScopeService,
        private readonly $location: ng.ILocationService,
        private readonly $injector: ng.auto.IInjectorService,
        private readonly $uibModal: any,
        private readonly blockUI: any,
        private readonly session: IOmniaSession,
        public readonly i18n: I18nService,
        public readonly config: OmniaConfig,
        private readonly apiState = session.apiState,
        private readonly sessionState = session.sessionState
    ) {

    }

    public setup($route: ngRoute.IRouteService) {

        this.$route = $route

        this.$rootScope.$on("$routeChangeSuccess", (_, route: ngRoute.ICurrentRoute) => this.onRouteChanged(route))

        this.$rootScope.$watch(() => this.currentActivity, (a, b) => {
            if (a != b) {
                this.notifyCurrentActivity(a)
            }
        });

        this.setupAutoRefresh()

    }

    private log(...data: any[]) {
        if (this.debug) {
            console.log(...data)
        }
    }

    get production() {
        return this.session.production
    }

    isReadOnly(selector?: (p: UserPermissions) => ReadWritePermissions) {
        return this.session.isReadOnly(selector)
    }

    get permissions() {
        return this.session.permissions
    }

    get debug() {
        return this.session.debugMode
    }

    get conflict() {
        return this._currentModule?.conflict
    }

    get sessionId() {
        return this.session.sessionId
    }

    get userId() {
        return this.session.currentUser?.Id
    }

    public setCurrentModule(module: IModule) {

        this._currentModule = module

        const state = {
            moduleId: module?.moduleId
        }

        this.session.setClientSate(state)

    }

    private notifyCurrentActivity(activity: ClientActivity) {

        const module = this._currentModule

        const state = {
            moduleId: module?.moduleId,
            activity
        }

        this.session.setClientSate(state)

    }

    public getCurrentModule(): IModule {
        return this._currentModule
    }

    /**
     * This is called when the current route changes, and is only used
     * to record the parameters for this route.
     *
     * If the route has parameters, they are saved in "this._currentRouteParams",
     * and then the location is changed to clear those parameters.
     *
     * The last saved parameters will remain unchanged until the "module"
     * (i.e., the first path of the route) does not change
     *
     * The reason for all this is that route parameters are used very infrequently in the app,
     * so that most state transitions occur without the route changing.
     *
     * As not clearing the route parameters in this scenario leads to weird behaviours
     * if using the browser's history buttons, they are always cleared.
     */
    private onRouteChanged(route: ngRoute.ICurrentRoute) {

        // This is undocumented, but provides the original path for the route
        // (i.e., the path template defined provided when the route was registered)
        const path: string = route["$$route"]["originalPath"] ?? ""

        // The first part of the path (ignoring the leading /) always identifies the module
        const r = path.split('/')[1]

        // Parameters for the current route
        const p = angular.extend({}, route.params)


        if (r !== this._currentRoute) {

            this._currentRoute = r
            this._currentRouteParams = p

            // If the module has changed (condition above), and the new path has parameters,
            // then replace the location to clear those paramters
            if (!isObjectEmpty(p)) {
                this.$location.path('/' + r)
                this.$location.replace()
            }

        }

        this.disableMenu = false

    }

    get currentActivity() {

        const m = this._currentModule

        let activity = m?.getCurrentActivity()

        if (!activity) {
            activity = this._dialogs.map(d => d.scope["omnia:activity"]).find(a => !!a)
        }

        return activity

    }

    defer(): ng.IDeferred<any> {
        return this.$q.defer()
    }

    resolve<T>(value: T): ng.IPromise<T> {
        return this.$q.resolve(value)
    }

    timeout(delay: number, invokeApply?: boolean): ng.IPromise<any> {
        return this.$timeout(delay, invokeApply)
    }

    disableShell(disable: boolean) {

        const set = (value: boolean) => {
            this.disableMenu = value
            this.$rootScope.$applyAsync()
        }

        const current = this.disableMenu

        if (disable != current) {
            set(disable)
            return () => set(current)
        }

    }

    showMessageBox(options: IMessageBox): ng.IPromise<any> {

        const scope = <IDialogScope>(this.$rootScope.$new(true))

        scope.options = options

        const template = options?.templateUrl
            ? "<omnia-shell-msgbox-tmpl></omnia-shell-msgbox-tmpl>"
            : "<omnia-shell-msgbox></omnia-shell-msgbox>"

        const args = <any>{
            template,
            scope,
            backdrop: options?.backdrop ? "static" : undefined,
            keyboard: Boolean(options?.closeOnEscape),
        }

        const instance = this.$uibModal.open(args)

        return instance.result

    }

    showTemplateAsDialog(template: string, args?: any, options?: IOmniaShellDialogOptions): ng.IPromise<any> {
        return this.showDialog(template, args, options)
    }

    showComponentAsDialog(component: string | AngularReactDialog, args?: any, options?: IOmniaShellDialogOptions): ng.IPromise<any> {

        if (isString(component)) {
            component = "<" + component + "></" + component + ">"
        }

        return this.showDialog(component, args, options)

    }

    showDialog(content: string | AngularReactDialog, args?: any, options?: IOmniaShellDialogOptions): ng.IPromise<any> {

        const scope = <IDialogScope>(this.$rootScope.$new(true))

        options = options || {}

        scope["arguments"] = args

        const dialog = { scope }
        this._dialogs.splice(0, 0, dialog)

        if (isString(content)) {

            // Angular directive

            const template = content

            this.log("dialog open", template)

            const instance = this.$uibModal.open({
                template,
                scope,
                size: options.size,
                windowClass: options.windowClass,
                backdrop: isUndefinedOrNull(options.backdrop) ? "static" : options.backdrop,
                keyboard: options.closeOnEscape !== false,
            })

            const result = instance.result as ng.IPromise<any>

            return result.finally(() => {
                this._dialogs.remove(d => d == dialog)
                this.log("dialog closed", template)
            })

        }
        else {

            // React function/component

            this.log("react dialog open", content.component.name)

            const component = content

            const result = showAngularReactDialog(
                this.$injector,
                component,
                args,
                options
            )

            return result.finally(() => {
                this._dialogs.remove(d => d == dialog)
                this.log("react dialog closed", content.component.name)
            })

        }

    }


    getSession(): IOmniaSession {
        return this.session
    }

    getRouteParams(): { [key: string]: any } {

        const p = this._currentRouteParams || []
        this._currentRouteParams = null

        return p

    }


    get enableAutoRefresh() {
        return this._enableAutoRefresh
    }

    set enableAutoRefresh(value) {
        this._enableAutoRefresh = value
        if (value) {
            this.refresh(true)
        }
    }

    canRefresh() {
        const { _currentModule: mod, _refreshing, session, disableMenu } = this
        return (_refreshing || session.isApiRefreshPending() || disableMenu)
            ? false
            : mod
                ? mod.canRefresh()
                : true
    }


    private setupAutoRefresh() {

        const enabled = () => {
            return this.enableAutoRefresh && (this._currentModule?.enableAutoRefresh ?? true) && isArrayEmpty(this._dialogs)
        }

        this.$rootScope.$on("omnia.refresh", (e, events: HoldingEvent[]) => this.refreshInternal(events))

        this.$rootScope.$watch(enabled, (a: boolean, b: boolean) => {

            if (a && (!b) && this._deferredAutoRefresh) {

                const events = this._deferredAutoRefresh.newEvents
                this._deferredAutoRefresh = undefined

                if (!isArrayEmpty(events)) {
                    this.refreshInternal(events)
                }

            }

        })

    }

    private refreshInternal(newEvents: HoldingEvent[]) {

        if (!this._enableAutoRefresh) {
            return
        }

        const enabled = this.getCurrentModule()?.enableAutoRefresh

        this.log("refreshInternal", enabled)

        if (!enabled) {

            if (this._deferredAutoRefresh) {
                this._deferredAutoRefresh = {
                    newEvents: this._deferredAutoRefresh.newEvents.concat(newEvents)
                }
            }

            else {
                this._deferredAutoRefresh = { newEvents }
            }

            this.log("refreshInternal deferred", this._deferredAutoRefresh)

            return

        }

        if (newEvents?.some(e => (e.Action == "u") && (e.ObjectType == OmniaObjectType.Holding))) {
            return this.session.refreshHoldings().then(() => this.refresh(true))
        }
        else {
            return this.refresh(true)
        }

    }

    refresh(auto: boolean) {

        if (this._refreshing) {
            this._refreshPending = auto ? "auto" : "user"
            return this.$q.resolve(null)
        }

        this.apiState.recordChange({ source: auto ? "refresh" : "refresh-all", local: true })

        const { _currentModule: mod } = this

        if (!mod) {
            return this.$q.resolve(null)
        }

        const blockUI = this.sessionState.disableBlockUI

        if (!auto) {
            this.startBlockUI()
        }
        else {
            this.sessionState.disableBlockUI = true
        }

        this._refreshing = mod.refresh(auto)
            .then(() => {

                const cmp = mod.getCurrentComponent()

                if (cmp instanceof UpdatableComponent) {
                    return cmp.refresh(auto)
                }

            })
            .finally(() => {

                this._refreshing = undefined
                this.sessionState.disableBlockUI = blockUI

                if (!auto) {
                    this.stopBlockUI()
                }

                if (this._refreshPending) {
                    auto = this._refreshPending == "auto"
                    this._refreshPending = undefined
                    return this.$timeout(5000).then(() => this.refresh(auto))
                }

            })

        return this._refreshing

    }

    startBlockUI(message?: string) {
        this.blockUI.start(message)
    }

    stopBlockUI() {
        this.blockUI.stop()
    }

    disableBlockUI(run: () => PromiseLike<any>) {
        return this.session.disableBlockUI(run)
    }

    reloadCurrentModule() {
        this.$route.reload()
    }

    loadScript(src: string) {
        const d = this.$q.defer()
        loadScript(src, () => d.resolve())
        return d.promise
    }

    loadStylesheet(href: string) {
        const d = this.$q.defer()
        loadStylesheet(href, () => d.resolve())
        return d.promise
    }

    navigate(path: string) {
        return this.$location.path(path)
    }

}

export function confirmAll(steps: (() => ng.IPromise<boolean>)[]): ng.IPromise<boolean> {
    return steps.reduce((p, step) => p ? p.then(ok => ok ? step() : false) : step(), null as ng.IPromise<any>)
}

function isMobileOperatingSystem() {

    var userAgent = navigator.userAgent || navigator.vendor

    if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i) || userAgent.match(/iPod/i) || userAgent.match(/Android/i)) {
        return true

    }

    return false
}
